import { Item, ItemType } from '@inno-sdks/files'
import { AppId } from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'

import { ItemModalParams, ItemQueryParams } from '../enums/item'
import { isClusterOfficeFile } from './clusterOffice'
import { isDrawingFile } from './isDrawingFile'

/**
 * Constructs a URL link for a given item based on its type and properties.
 *
 * - For files recognized as Cluster Office files, returns a Cluster Office app URL.
 * - For drawings, returns a drawing editor URL.
 * - For other files, returns a link with the item ID as a query parameter.
 * - For folders, returns a folder-specific link.
 *
 * @param {Item} item - The item object containing type and id.
 * @returns {string} The constructed URL string pointing to the item.
 */
export function getItemLink(
  item: Item,
  apps: ReturnType<typeof useAvailableApps>['apps']
): string {
  const url = new URL(window.location.origin + apps[AppId.CLUSTER_FILES].path)
  const params = new URLSearchParams()

  if (item.type === ItemType.File) {
    const currentParams = new URLSearchParams(url.search)
    const folderId = currentParams.get(ItemQueryParams.FolderId)

    // Persist current folder
    if (folderId) {
      params.set(ItemQueryParams.FolderId, folderId)
    }

    if (isClusterOfficeFile(item)) {
      params.set(ItemModalParams.Office, String(item.id))
    } else if (isDrawingFile(item)) {
      params.set(ItemModalParams.Drawing, String(item.id))
    } else {
      params.set(ItemModalParams.Properties, String(item.id))
    }
  } else if (item.type === ItemType.Folder) {
    params.set(ItemQueryParams.FolderId, String(item.id))
  } else if (item.type === ItemType.Shortcut) {
    return item.metaData.shortcutUrl
  }

  url.search = params.toString()
  return url.toString()
}
